/**
 * Version Resolution Bootstrap
 *
 * GW-12170 (FR-1/FR-1a/FR-1b/FR-2/FR-3 of PRP-INF-009):
 * Resolves the effective app version from URL query params and localStorage.
 *
 * Resolution order: ?version= > ?gwVersion= > localStorage['currentVersion'] > 'latest'
 *
 * Only values matching VERSION_REGEX are accepted. Invalid values resolve
 * to 'latest' and are never persisted to storage.
 *
 * The resolved sha is returned for the caller (GW-12179) to inject into the
 * bootstrap script slot in index.html. When the effective version is 'latest',
 * returns 'latest' and no DOM injection is needed.
 */

import { STORAGE_KEYS } from '@/constants/storageKeys';

/** Accepts 'latest' or any 7–40 character lowercase hex sha. */
export const VERSION_REGEX = /^(latest|[0-9a-f]{7,40})$/;

/**
 * Returns true when `value` is a safe, allowed version string.
 */
export function isValidVersion(value: string): boolean {
  return VERSION_REGEX.test(value);
}

/**
 * Resolves the effective version.
 *
 * Steps:
 *  1. Read `version` then `gwVersion` from `params` (`version` wins when both present).
 *  2. Validate the raw param value with VERSION_REGEX. Invalid → `latest`, not persisted.
 *  3. If valid URL param present, write to `storage['currentVersion']`.
 *  4. Effective version = valid URL param ?? valid stored value ?? 'latest'.
 *  5. `isVersionDeployed` callback (default: always true) lets callers implement
 *     stale-sha fallback. When it returns false for the effective sha, falls back
 *     to 'latest' without touching storage.
 *
 * @param params - URLSearchParams to read from (defaults to the live search string)
 * @param storage - Storage implementation (defaults to window.localStorage)
 * @param isVersionDeployed - Optional predicate; return false to trigger stale-sha fallback
 */
export function resolveVersion(
  params: URLSearchParams = new URLSearchParams(window.location.search),
  storage: Storage = window.localStorage,
  isVersionDeployed: (sha: string) => boolean = () => true
): string {
  const rawParam = params.get('version') ?? params.get('gwVersion') ?? null;

  let urlVersion: string | null = null;
  if (rawParam !== null) {
    urlVersion = isValidVersion(rawParam) ? rawParam : null;
    if (urlVersion !== null) {
      storage.setItem(STORAGE_KEYS.CURRENT_VERSION, urlVersion);
    }
    // Invalid param: do not persist, resolve to 'latest' below
  }

  if (urlVersion !== null) {
    if (urlVersion === 'latest') return 'latest';
    return isVersionDeployed(urlVersion) ? urlVersion : 'latest';
  }

  const stored = storage.getItem(STORAGE_KEYS.CURRENT_VERSION);
  if (stored !== null && isValidVersion(stored)) {
    if (stored === 'latest') return 'latest';
    return isVersionDeployed(stored) ? stored : 'latest';
  }

  return 'latest';
}

/**
 * GW-12287: Resolve the React Router basename.
 *
 * Vite's `base` doubles as both the asset public-path prefix AND import.meta.env.BASE_URL.
 * A pinned per-SHA build is compiled with base=/<sha>/ but version-bootstrap injects it while
 * the browser URL stays at / (it never navigates). So the router basename must equal the build
 * base ONLY when the browser is actually located under that base; otherwise it must be '/'.
 * This is derived from ground truth (location vs base), independent of how the pinned build was
 * selected (query param OR localStorage), so a param-stripped reload of a pinned build still
 * renders.
 */
export function resolveRouterBasename(
  base: string = import.meta.env.BASE_URL,
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '/'
): string {
  if (base === '/') return '/';
  return pathname.startsWith(base) ? base : '/';
}
